import { FILE_CHUNK_SIZE_IN_BYTES } from 'lib/constants/s3FileUpload';
import { CommonLanguage } from 'lib/datasets/languages';
import { getVideoDuration } from 'lib/helpers/videoFileHelpers';
import {
  IS3FileMultipartUploadPartWithEtag,
  IS3FileMultipartUploadPartWithPresignedUrl,
  PrivateFileFolder,
  PublicFileFolder,
  SensitiveFileFolder,
} from 'lib/types/s3-files';
import _ from 'lodash';
import fileType from 'magic-bytes.js';
import { GuessedFile } from 'magic-bytes.js/dist/model/tree';
import {
  ICreateMultipartUploadPayload,
  IPolicyBotCompleteMultipartUploadPayload,
  IPolicyBotCreateMultipartUploadPayload,
  IPrivateCompleteMultipartUploadPayload,
  IPublicCompleteMultipartUploadPayload,
  ISensitiveCompleteMultipartUploadPayload,
  IVideoCompleteMultipartUploadPayload,
  IVideoCreateMultipartUploadPayload,
  s3CompleteMultipartVideoUpload,
  s3CreateMultipartVideoUpload,
  s3PolicyBotCompleteMultipartUpload,
  s3PolicyBotCreateMultipartUpload,
  s3PresignedUrlUpload,
  s3PrivateCompleteMultipartUpload,
  s3PrivateCreateMultipartUpload,
  s3PublicCompleteMultipartUpload,
  s3PublicCreateMultipartUpload,
  s3SensitiveCompleteMultipartUpload,
  s3SensitiveCreateMultipartUpload,
} from './api';
import { IS3UploadAdapter } from './hooks';
import { UploadDomain } from './types';

/*****************
 * S3 ADAPTERS
 *****************/
enum S3UploadStatus {
  ABORTED = 'ABORTED',
  FAILURE = 'FAILURE',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
}

export interface IS3UploadUrls {
  ADMIN: string;
  INTERNAL: string;
}

const PUBLIC_CREATE_MULTIPART_UPLOAD_API_URL: IS3UploadUrls = {
  ADMIN: '/admin/files/public-multipart-upload',
  INTERNAL: '/internal/files/multipart-upload',
};

const PUBLIC_COMPLETE_MULTIPART_UPLOAD_API_URL: IS3UploadUrls = {
  ADMIN: '/admin/files/complete-public-upload',
  INTERNAL: '/internal/files/complete-upload',
};

export type UploadSuccessHandler = (fileMetadata: {
  fileId: number;
  fileUrl?: string;
  fileName: string;
}) => void;

export class S3Uploader {
  // Most browsers allow 6 simultaneous requests per domain
  private static SIMULTANEOUS_REQUEST_COUNT = 6;

  private _status: S3UploadStatus;
  private controller: AbortController = new AbortController();

  protected file: File;
  protected accept: string;
  protected partsCompleted: number = 0;

  fileId: number;
  fileUrl: string;
  partsWithUrls: IS3FileMultipartUploadPartWithPresignedUrl[];
  uploadPercentage: number = 0;

  onAbort: () => void;
  onError: (message?: string) => void;
  onProgress: (uploadPercentage: number) => void;
  onSuccess: UploadSuccessHandler;

  constructor({
    file,
    partsWithUrls,
    accept,
  }: {
    file: File;
    partsWithUrls: IS3FileMultipartUploadPartWithPresignedUrl[];
    accept: string;
  }) {
    this.file = file;
    this.partsWithUrls = partsWithUrls;
    this.accept = accept;
  }

  get numberOfParts(): number {
    return getNumberOfParts(this.file.size);
  }

  set status(status: S3UploadStatus) {
    this._status = status;
    if (status === S3UploadStatus.ABORTED) {
      this.onAbort?.();
    } else if (status === S3UploadStatus.FAILURE) {
      this.onError?.();
    } else if (status === S3UploadStatus.PENDING) {
      this.onProgress?.(this.uploadPercentage);
    } else if (status === S3UploadStatus.SUCCESS) {
      this.onSuccess?.({
        fileId: this.fileId,
        fileUrl: this.fileUrl,
        fileName: this.file.name,
      });
    }
  }

  private async uploadParts(
    parts: IS3FileMultipartUploadPartWithPresignedUrl[]
  ): Promise<IS3FileMultipartUploadPartWithEtag[]> {
    const resolvedParts: Response[] = [];
    const partsChunks = _.chunk(parts, S3Uploader.SIMULTANEOUS_REQUEST_COUNT);
    for (const partsChunk of partsChunks) {
      try {
        const responses = await Promise.all<Response>(
          _.map(partsChunk, (x) => this.presignedUrlUpload(x))
        );
        resolvedParts.push(...responses);
      } catch (e) {
        this.uploadPercentage = 0;
        if (e.name === 'AbortError') {
          this.status = S3UploadStatus.ABORTED;
        } else {
          this.status = S3UploadStatus.FAILURE;
        }
        return;
      }
    }

    const partsWithEtag: IS3FileMultipartUploadPartWithEtag[] = [];
    for (const [index, part] of Object.entries(resolvedParts)) {
      partsWithEtag.push({
        etag: part.headers.get('etag'),
        partNumber: Number(index) + 1,
      });
    }
    return partsWithEtag;
  }

  protected async getGuessedFiles(): Promise<GuessedFile[]> {
    // this.file.arrayBuffer() fails if the file is too large. (2GB in Chrome)
    // The first few bytes in the file are what determine the filetype, so looking at
    // the first chunk is sufficient. A chunk is roughly 64kB.
    // https://en.wikipedia.org/wiki/List_of_file_signatures
    const reader = this.file.stream().getReader();
    const chunk = await reader.read();
    return fileType(chunk.value);
  }

  protected getIsValidFileType(mimeType: string) {
    const allowedMimeTypes = _.chain(this.accept)
      .split(',')
      .map((ext) => _.trimStart(ext, '.'))
      .value();
    return _.includes(allowedMimeTypes, mimeType);
  }

  async abort() {
    this.controller.abort();
  }

  async multipartUpload() {
    const partsWithEtags = await this.uploadParts(this.partsWithUrls);
    if (this._status !== S3UploadStatus.PENDING) {
      return;
    }

    return partsWithEtags;
  }

  async presignedUrlUpload(
    part: IS3FileMultipartUploadPartWithPresignedUrl
  ): Promise<Response> {
    const onUploadPartSuccess = () => {
      this.partsCompleted++;
      this.uploadPercentage = Math.ceil(
        (this.partsCompleted / this.numberOfParts) * 100
      );
      this.status = S3UploadStatus.PENDING;
    };

    const start = (part.partNumber - 1) * FILE_CHUNK_SIZE_IN_BYTES;
    const end = part.partNumber * FILE_CHUNK_SIZE_IN_BYTES;
    const blob =
      part.partNumber < this.numberOfParts
        ? this.file.slice(start, end)
        : this.file.slice(start);
    const options = { signal: this.controller.signal };

    return s3PresignedUrlUpload({
      url: part.presignedUrl,
      blob,
      onSuccess: onUploadPartSuccess,
      options,
    });
  }

  async validateFileType() {
    if (this.accept === '*') {
      return true;
    }
    const guessedFiles = await this.getGuessedFiles();
    const isValidFileType = _.some(guessedFiles, (guessedFile) =>
      this.getIsValidFileType(guessedFile.extension)
    );
    if (!isValidFileType) {
      this.onError?.('Invalid file type');
    }
    return isValidFileType;
  }
}

export class S3PrivateFileUploader extends S3Uploader {
  private folder: string;

  constructor({
    file,
    folder,
    accept,
  }: {
    file: File;
    folder: string;
    accept: string;
  }) {
    super({ file, partsWithUrls: [], accept });
    this.folder = folder;
  }

  async uploadFile() {
    const isValidFileType = await this.validateFileType();
    if (!isValidFileType) {
      return;
    }

    this.status = S3UploadStatus.PENDING;

    const createPayload: ICreateMultipartUploadPayload = {
      numberOfParts: this.numberOfParts,
      fileName: this.file.name,
      folder: this.folder,
    };
    const createRes = await s3PrivateCreateMultipartUpload(createPayload);
    if (createRes.error) {
      this.uploadPercentage = 0;
      this.status = S3UploadStatus.FAILURE;
      return;
    }

    this.partsWithUrls = createRes.data.parts;
    const partsWithEtags = await this.multipartUpload();

    const completePayload: IPrivateCompleteMultipartUploadPayload = {
      parts: partsWithEtags,
      privateFileId: createRes.data.privateFileId,
      uploadId: createRes.data.uploadId,
    };
    const completeRes = await s3PrivateCompleteMultipartUpload(completePayload);
    if (completeRes.error) {
      this.uploadPercentage = 0;
      this.status = S3UploadStatus.FAILURE;
    } else {
      this.uploadPercentage = 100;
      this.fileId = completeRes.data.privateFileId;
      this.status = S3UploadStatus.SUCCESS;
      return completeRes.data;
    }
  }
}

export class S3PublicFileUploader extends S3Uploader {
  private createApiUrl: string;
  private completeApiUrl: string;
  private folder: string;

  constructor({
    file,
    accept,
    domain,
    folder,
  }: {
    file: File;
    accept: string;
    domain: keyof IS3UploadUrls;
    folder: string;
  }) {
    super({ file, partsWithUrls: [], accept });
    this.folder = folder;
    this.createApiUrl = PUBLIC_CREATE_MULTIPART_UPLOAD_API_URL[domain];
    this.completeApiUrl = PUBLIC_COMPLETE_MULTIPART_UPLOAD_API_URL[domain];
  }

  async uploadFile() {
    const isValidFileType = await this.validateFileType();
    if (!isValidFileType) {
      return;
    }

    this.status = S3UploadStatus.PENDING;

    const createPayload: ICreateMultipartUploadPayload = {
      numberOfParts: this.numberOfParts,
      fileName: this.file.name,
      folder: this.folder,
    };
    const createRes = await s3PublicCreateMultipartUpload(
      this.createApiUrl,
      createPayload
    );
    if (createRes.error) {
      this.uploadPercentage = 0;
      this.status = S3UploadStatus.FAILURE;
      return;
    }

    this.partsWithUrls = createRes.data.parts;
    const partsWithEtags = await this.multipartUpload();

    const completePayload: IPublicCompleteMultipartUploadPayload = {
      parts: partsWithEtags,
      publicFileId: createRes.data.publicFileId,
      uploadId: createRes.data.uploadId,
    };
    const completeRes = await s3PublicCompleteMultipartUpload(
      this.completeApiUrl,
      completePayload
    );
    if (completeRes.error) {
      this.uploadPercentage = 0;
      this.status = S3UploadStatus.FAILURE;
    } else {
      this.uploadPercentage = 100;
      this.fileId = completeRes.data.publicFileId;
      this.fileUrl = completeRes.data.fileUrl;
      this.status = S3UploadStatus.SUCCESS;
      return completeRes.data;
    }
  }
}

const getAdminPolicyBotFilesAdapter = (name: string): IS3UploadAdapter => ({
  buildCreatePayload: async ({
    file,
    guessedFile,
  }): Promise<IPolicyBotCreateMultipartUploadPayload> => ({
    fileName: file.name,
    fileExtension: guessedFile.extension,
    numberOfParts: getNumberOfParts(file.size),
  }),
  buildCompletePayload: ({
    entityId,
    parts,
    uploadId,
  }): IPolicyBotCompleteMultipartUploadPayload => ({
    id: entityId,
    multipartUploadParts: parts,
    multipartUploadId: uploadId,
    name,
  }),
  createMultipartUploadAndTranslateResponse: async (
    payload: IPolicyBotCreateMultipartUploadPayload
  ) => {
    const res = await s3PolicyBotCreateMultipartUpload(payload);
    if (res.error) {
      throw new Error(res.message);
    }
    return {
      parts: res.data.multipartUploadParts,
      entityId: res.data.id,
      uploadId: res.data.multipartUploadId,
    };
  },
  completeMultipartUpload: s3PolicyBotCompleteMultipartUpload,
});

const getAdminPrivateFilesAdapter = (
  folder: PrivateFileFolder
): IS3UploadAdapter => ({
  buildCreatePayload: async ({
    file,
  }): Promise<ICreateMultipartUploadPayload> => ({
    fileName: file.name,
    folder,
    numberOfParts: getNumberOfParts(file.size),
  }),
  buildCompletePayload: ({
    entityId,
    parts,
    uploadId,
  }): IPrivateCompleteMultipartUploadPayload => ({
    parts,
    privateFileId: entityId,
    uploadId,
  }),
  createMultipartUploadAndTranslateResponse: async (
    payload: ICreateMultipartUploadPayload
  ) => {
    const res = await s3PrivateCreateMultipartUpload(payload);
    if (res.error) {
      throw new Error(res.message);
    }
    return {
      parts: res.data.parts,
      entityId: res.data.privateFileId,
      uploadId: res.data.uploadId,
    };
  },
  completeMultipartUpload: s3PrivateCompleteMultipartUpload,
});

const getAdminPublicFilesAdapter = (
  folder: PublicFileFolder
): IS3UploadAdapter => ({
  buildCreatePayload: async ({
    file,
  }): Promise<ICreateMultipartUploadPayload> => ({
    fileName: file.name,
    folder,
    numberOfParts: getNumberOfParts(file.size),
  }),
  buildCompletePayload: ({
    entityId,
    parts,
    uploadId,
  }): IPublicCompleteMultipartUploadPayload => ({
    parts,
    publicFileId: entityId,
    uploadId,
  }),
  createMultipartUploadAndTranslateResponse: async (
    payload: ICreateMultipartUploadPayload
  ) => {
    const res = await s3PublicCreateMultipartUpload(
      '/admin/files/public-multipart-upload',
      payload
    );
    if (res.error) {
      throw new Error(res.message);
    }
    return {
      parts: res.data.parts,
      entityId: res.data.publicFileId,
      uploadId: res.data.uploadId,
    };
  },
  completeMultipartUpload: _.partial(
    s3PublicCompleteMultipartUpload,
    '/admin/files/complete-public-upload'
  ),
});

const getAdminVideosAdapter = (language: string): IS3UploadAdapter => ({
  buildCreatePayload: async ({
    file,
    guessedFile,
  }): Promise<IVideoCreateMultipartUploadPayload> => ({
    durationInSeconds: await getVideoDuration(file),
    fileName: file.name,
    language,
    numberOfParts: getNumberOfParts(file.size),
    videoFileExtension: guessedFile.extension,
  }),
  buildCompletePayload: ({
    entityId,
    parts,
    uploadId,
  }): IVideoCompleteMultipartUploadPayload => ({
    videoId: entityId,
    parts,
    uploadId,
  }),
  createMultipartUploadAndTranslateResponse: async (
    payload: IVideoCreateMultipartUploadPayload
  ) => {
    const res = await s3CreateMultipartVideoUpload(payload);
    if (res.error) {
      throw new Error(res.message);
    }
    return {
      parts: res.data.videoUploadParts,
      entityId: res.data.videoId,
      uploadId: res.data.videoMultipartUploadId,
    };
  },
  completeMultipartUpload: s3CompleteMultipartVideoUpload,
});

/**
 * TODO (HCM): Replace with cases attachments adapter
 */
const getConcernAttachmentsAdapter = (
  companyUrlAlias: string,
  folder: SensitiveFileFolder
): IS3UploadAdapter => ({
  buildCreatePayload: async ({
    file,
  }): Promise<ICreateMultipartUploadPayload> => ({
    fileName: file.name,
    folder,
    numberOfParts: getNumberOfParts(file.size),
  }),
  buildCompletePayload: ({
    entityId,
    parts,
    uploadId,
  }): ISensitiveCompleteMultipartUploadPayload => ({
    parts,
    sensitiveFileId: entityId,
    uploadId,
  }),
  createMultipartUploadAndTranslateResponse: async (
    payload: ICreateMultipartUploadPayload
  ) => {
    const res = await s3SensitiveCreateMultipartUpload(
      companyUrlAlias,
      payload
    );
    if (res.error) {
      throw new Error(res.message);
    }
    return {
      parts: res.data.parts,
      entityId: res.data.sensitiveFileId,
      uploadId: res.data.uploadId,
    };
  },
  completeMultipartUpload: (
    payload: ISensitiveCompleteMultipartUploadPayload
  ) => s3SensitiveCompleteMultipartUpload(companyUrlAlias, payload),
});

export const getUploadAdapter = (
  domain: UploadDomain,
  options?: {
    policyBotFileName?: string;
    publicFileFolder?: PublicFileFolder;
    privateFileFolder?: PrivateFileFolder;
    sensitiveFileFolder?: SensitiveFileFolder;
    videoLanguage?: CommonLanguage;
    companyUrlAlias?: string;
  }
): IS3UploadAdapter => {
  switch (domain) {
    case UploadDomain.ADMIN_POLICY_BOT_FILES:
      return getAdminPolicyBotFilesAdapter(options.policyBotFileName);

    case UploadDomain.ADMIN_PRIVATE_FILES:
      if (!options.privateFileFolder) {
        throw new Error('Private file folder is required');
      }
      return getAdminPrivateFilesAdapter(options.privateFileFolder);

    case UploadDomain.ADMIN_PUBLIC_FILES:
      if (!options.publicFileFolder) {
        throw new Error('Public file folder is required');
      }
      return getAdminPublicFilesAdapter(options.publicFileFolder);

    case UploadDomain.ADMIN_VIDEOS:
      if (!options.videoLanguage) {
        throw new Error('Video language is required');
      }
      return getAdminVideosAdapter(options.videoLanguage);

    case UploadDomain.CONCERN_ATTACHMENTS:
      if (!options.sensitiveFileFolder || !options.companyUrlAlias) {
        throw new Error(
          'Sensitive file folder and company URL alias are required'
        );
      }
      return getConcernAttachmentsAdapter(
        options.companyUrlAlias,
        options.sensitiveFileFolder
      );

    // TODO internal domains

    default:
      throw new Error('Invalid domain');
  }
};
/*****************
 * END S3 ADAPTERS
 *****************/

export const validateFileType = async (
  accept: string,
  file: File
): Promise<{
  isValid: boolean;
  guessedFile: GuessedFile;
}> => {
  const reader = file.stream().getReader();
  const chunk = await reader.read();
  const guessedFiles = fileType(chunk.value);

  if (accept === '*') {
    return {
      guessedFile: guessedFiles[0],
      isValid: true,
    };
  }

  const allowedMimeTypes = _.chain(accept)
    .split(',')
    .map((ext) => _.trimStart(ext, '.'))
    .value();
  const isValid = _.some(guessedFiles, (guessedFile) =>
    allowedMimeTypes.includes(guessedFile.extension)
  );
  if (!isValid) {
    throw new Error('Invalid file type');
  }

  const guessedFile = _.find(guessedFiles, (guessedFile) =>
    _.includes(allowedMimeTypes, guessedFile.extension)
  );

  return {
    guessedFile,
    isValid,
  };
};

export const getNumberOfParts = (sizeInBytes: number): number => {
  return Math.ceil(sizeInBytes / FILE_CHUNK_SIZE_IN_BYTES);
};
